/**
 * <TutorStudentDiagnosticCard> — le diagnostic pédagogique, en tête des rapports de l'élève (organism).
 *
 * L'ancienne `DiagnosticModal.js` en lecture (`editable={false}`), sans modale : la date du rapport
 * et sa place (« 20/10/2026 · 1/3 »), le texte, et deux flèches quand il y en a plusieurs. Un rapport
 * sans diagnostic le dit par la phrase grise du legacy. L'édition et la génération en cours restent à
 * la fin de séance. Un échec se dit, l'ancien écran ne disait rien. Contrôlée : `index` arrive en prop.
 */
import React from "react"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Text } from "../../atoms/typography/Text"
import { SettingsSection } from "../../molecules/SettingsSection"
import { type UITutorStudentDiagnostic } from "../../../ports/primary/ui/types/tutorEleves"

const TEXTS = {
    title: "Diagnostic pédagogique",
    placeholder: "Diagnostic de l'élève...",
    previous: "Rapport précédent",
    next: "Rapport suivant",
    error: "Le diagnostic n'a pas pu se lire.",
    retry: "Réessayer",
}

const ARROW_SIZE = 32

export type TutorStudentDiagnosticCardProps = {
    /** Un diagnostic par rapport, du plus récent au plus ancien. */
    diagnostics: UITutorStudentDiagnostic[]
    index?: number
    onChangeIndex?: (index: number) => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorStudentDiagnosticCard(props: TutorStudentDiagnosticCardProps) {
    const { diagnostics, index = 0, onChangeIndex, loading = false, error, onRetry, testID } = props
    const current = diagnostics[index] ?? null
    const several = diagnostics.length > 1
    const text = current?.text != null && current.text.trim() !== "" ? current.text : null

    if (!loading && error == null && diagnostics.length === 0) return null

    return (
        <SettingsSection title={TEXTS.title} testID={testID}>
            {loading ? (
                <DelayedSkeleton accessibilityLabel="Chargement du diagnostic">
                    <Stack gap="1">
                        <Skeleton textSize="text-md" />
                        <Skeleton textSize="text-md" />
                        <Skeleton textSize="text-md" width="60%" />
                    </Stack>
                </DelayedSkeleton>
            ) : error != null ? (
                <Row gap="2" align="center">
                    <Text size="text-md" color="text-danger">
                        {TEXTS.error}
                    </Text>
                    {onRetry != null && <TextButton label={TEXTS.retry} onPress={onRetry} />}
                </Row>
            ) : (
                <Stack gap="2">
                    {current != null && (
                        <Row gap="1" align="center">
                            <Text size="text-sm" color="text-muted">
                                {several
                                    ? `${current.dateLabel} · ${index + 1}/${diagnostics.length}`
                                    : current.dateLabel}
                            </Text>
                            {several && (
                                <>
                                    <IconButton
                                        iconName="chevron-left"
                                        appearance="transparent"
                                        iconColor="primary"
                                        size={ARROW_SIZE}
                                        disabled={index === 0}
                                        onPress={() => onChangeIndex?.(index - 1)}
                                        accessibilityLabel={TEXTS.previous}
                                    />
                                    <IconButton
                                        iconName="chevron-right"
                                        appearance="transparent"
                                        iconColor="primary"
                                        size={ARROW_SIZE}
                                        disabled={index >= diagnostics.length - 1}
                                        onPress={() => onChangeIndex?.(index + 1)}
                                        accessibilityLabel={TEXTS.next}
                                    />
                                </>
                            )}
                        </Row>
                    )}
                    <Text size="text-md" color={text != null ? "text-primary" : "text-muted"}>
                        {text ?? TEXTS.placeholder}
                    </Text>
                </Stack>
            )}
        </SettingsSection>
    )
}
